import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { collection, getDocs, doc, setDoc, addDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import TechPrepPortalMgtBreadCrumbs from "../Layout/TechPrepPortalMgtBreadCrumbs";

/**
 * TpShowcaseToggles
 *
 * Manages on/off switches for all three public-facing Showcase forms:
 *   Form A — App A (main showcase application)  → TpShowFormStatus   (field: active "Yes"/"No")
 *   Form B — App B (secondary application)       → TpShowFormBStatus  (field: active "Yes"/"No")
 *   Form C — App C (tertiary application)        → TpShowFormCStatus  (field: active "Yes"/"No")
 *
 * The public TechPrepShowcaseForm already reads TpShowFormStatus to decide whether
 * to show/hide Form A. Form B and Form C will read their respective collections.
 */

const FORMS = [
  {
    key: "formA",
    label: "Form A — Showcase Application (App A)",
    description:
      "Controls access to the main public Tech Prep Showcase application form.",
    collection: "TpShowFormStatus",
  },
  {
    key: "formB",
    label: "Form B — Showcase Application (App B)",
    description:
      "Controls access to the secondary public Tech Prep Showcase application form.",
    collection: "TpShowFormBStatus",
  },
  {
    key: "formC",
    label: "Form C — Showcase Application (App C)",
    description:
      "Controls access to the tertiary public Tech Prep Showcase application form.",
    collection: "TpShowFormCStatus",
  },
];

const TpShowcaseToggles = () => {
  // State: { formA: { enabled, docId, loading }, formB: {...}, formC: {...} }
  const [formStates, setFormStates] = useState(
    Object.fromEntries(
      FORMS.map((f) => [f.key, { enabled: false, docId: null, loading: true }]),
    ),
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load current status for all forms on mount
  useEffect(() => {
    const loadAll = async () => {
      const updates = {};
      await Promise.all(
        FORMS.map(async (form) => {
          try {
            const snap = await getDocs(collection(db, form.collection));
            if (!snap.empty) {
              const d = snap.docs[0];
              const val = String(d.data().active || "").toLowerCase();
              const enabled = val === "yes" || val === "true" || val === "-1";
              updates[form.key] = {
                enabled,
                docId: d.id,
                loading: false,
              };
            } else {
              updates[form.key] = {
                enabled: false,
                docId: null,
                loading: false,
              };
            }
          } catch (err) {
            console.error(`Error loading ${form.collection}:`, err);
            updates[form.key] = { enabled: false, docId: null, loading: false };
          }
        }),
      );
      setFormStates((prev) => ({ ...prev, ...updates }));
    };

    loadAll();
  }, []);

  const handleToggle = async (formKey, collectionName, newVal) => {
    // Optimistic UI update
    setFormStates((prev) => ({
      ...prev,
      [formKey]: { ...prev[formKey], enabled: newVal, loading: true },
    }));

    try {
      const payload = {
        active: newVal ? "Yes" : "No",
        updated_at: new Date(),
      };

      const currentDocId = formStates[formKey].docId;

      if (currentDocId) {
        await setDoc(doc(db, collectionName, currentDocId), payload, {
          merge: true,
        });
        setFormStates((prev) => ({
          ...prev,
          [formKey]: { ...prev[formKey], enabled: newVal, loading: false },
        }));
      } else {
        const docRef = await addDoc(collection(db, collectionName), payload);
        setFormStates((prev) => ({
          ...prev,
          [formKey]: { enabled: newVal, docId: docRef.id, loading: false },
        }));
      }

      setSnackbar({
        open: true,
        message: `${FORMS.find((f) => f.key === formKey)?.label} turned ${newVal ? "ON" : "OFF"}`,
        severity: "success",
      });
    } catch (err) {
      console.error(`Error saving ${collectionName}:`, err);
      // Revert on error
      setFormStates((prev) => ({
        ...prev,
        [formKey]: { ...prev[formKey], enabled: !newVal, loading: false },
      }));
      setSnackbar({
        open: true,
        message: "Error saving form status. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth={false} sx={{ paddingTop: "50px" }}>
      <TechPrepPortalMgtBreadCrumbs
        pageName="Showcase Form Toggles"
        icon="faCog"
      />

      <Box mt={3}>
        <Typography variant="h5" gutterBottom>
          Showcase Form Toggles
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Use the switches below to open or close each public-facing Showcase
          application form. Changes take effect immediately.
        </Typography>

        <Grid container spacing={3}>
          {FORMS.map((form, idx) => {
            const state = formStates[form.key];
            return (
              <Grid item xs={12} md={4} key={form.key}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderTop: 4,
                    borderColor: state.enabled ? "success.main" : "grey.400",
                    transition: "border-color 0.3s",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {form.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: 40 }}
                  >
                    {form.description}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={state.enabled ? "success.main" : "text.secondary"}
                    >
                      {state.loading
                        ? "Saving..."
                        : state.enabled
                          ? "Open — Accepting Submissions"
                          : "Closed — Not Accepting Submissions"}
                    </Typography>
                    {state.loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={state.enabled}
                            onChange={(e) =>
                              handleToggle(
                                form.key,
                                form.collection,
                                e.target.checked,
                              )
                            }
                            color="success"
                          />
                        }
                        label={state.enabled ? "ON" : "OFF"}
                        labelPlacement="start"
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                    mt={1}
                  >
                    Firestore: {form.collection}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TpShowcaseToggles;
